import React, { useEffect, useState } from "react";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import LayoutIcon from "@/Components/LayoutIcon";
import { IconAi, Pie } from "@/Icons";
import AppLayout from "@/Layouts/AppLayout";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import Markdown from "react-markdown";
import { GrayBox } from "@/Components";
import Placeholder from "../../../Components/Placeholder";

export default function Insights(props) {
  return (
    <>
      {props.isLoading && (
        <div className="mt-16 flex flex-col items-center justify-center gap-2">
          <SvgProgressBar />

          <h2 className="text-xl font-bold">
            Your emission insights are on the way!
          </h2>
          <p>
            Analyzing emissions data to help you make more sustainable decisions
          </p>

          <p className="mt-8 rounded-sm bg-yellow px-4 py-3">
            Do not refresh, close or leave this page. Your data might be lost
          </p>
        </div>
      )}

      {props?.messages?.recommendations && !props.isLoading && (
        <>
          <GrayBox margin="my-8 bg-yellow">
            <div className="insights-wysiwyg prose max-w-none">
              <Markdown
                remarkPlugins={[remarkMath]}
                rehypePlugins={[rehypeKatex]}
              >
                {props.messages.recommendations}
              </Markdown>
            </div>
          </GrayBox>

          <h3 className="text-lg font-bold">Calculations</h3>
          <GrayBox margin="mt-4">
            <div className="insights-wysiwyg prose max-w-none">
              {props?.messages?.calculations === null && (
                <>
                  <Placeholder width="350px" height="20px" className="mb-4" />
                  <Placeholder width="700px" height="20px" className="mb-4" />
                  <Placeholder width="650px" height="20px" className="mb-4" />
                  <Placeholder width="320px" height="20px" className="mb-4" />
                  <Placeholder width="700px" height="20px" className="mb-8" />

                  <Placeholder width="650px" height="20px" className="mb-4" />
                  <Placeholder width="320px" height="20px" className="mb-4" />
                  <Placeholder width="500px" height="20px" className="mb-4" />
                </>
              )}
              {props?.messages?.calculations && (
                <>
                  <Markdown
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                  >
                    {props.messages.calculations}
                  </Markdown>
                </>
              )}
            </div>
          </GrayBox>
        </>
      )}
    </>
  );
}

Insights.layout = (page) => {
  return <Layout page={page}>{page}</Layout>;
};

const Layout = ({ page }) => {
  const { report } = page.props;

  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState({
    recommendations: null,
    calculations: null,
  });

  useEffect(() => {
    if (isLoading || messages.recommendations) {
      return;
    }
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);

      // Clear previous messages
      setMessages({
        recommendations: null,
        calculations: null,
      });

      // Start the analysis
      const initResponse = await axios.post(
        route("emissions.inventory.insights.init", report),
      );

      // If threadId is missing, stop the process
      if (!initResponse.data.threadId) {
        throw new Error("Thread ID is missing. Cannot proceed.");
      }

      // Process Step 1
      const step1Response = await axios.post(
        route("emissions.inventory.insights.step"),
        { step: 1 },
      );
      console.log(step1Response);
      setMessages((prev) => ({
        ...prev,
        recommendations: step1Response.data.message,
      }));

      // Process Step 2, run as async
      axios
        .post(route("emissions.inventory.insights.step"), { step: 2 })
        .then((response) => {
          console.log(response);
          setMessages((prev) => ({
            ...prev,
            calculations: response.data.message,
          }));
        });
    } catch (error) {
      console.error(
        error.message || "An error occurred while fetching insights.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppLayout title={`Insights - ${report.title}`}>
      <OrganizationLayout
        header={report.title}
        preamble="Inventory insights"
        action={
          <button
            className="btn flex justify-center gap-2"
            onClick={fetchInsights}
          >
            Generate new insights <IconAi />
          </button>
        }
        icon={
          <LayoutIcon>
            <Pie size={50} className="text-black" />
          </LayoutIcon>
        }
      >
        {React.cloneElement(page, { isLoading, messages })}
      </OrganizationLayout>
    </AppLayout>
  );
};

const SvgProgressBar = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(interval);
          return 100;
        }
        if (prevProgress >= 80) {
          return prevProgress + 0.1; // Slow down after 80%
        }
        return prevProgress + 0.5; // Normal speed
      });
    }, 50); // Reduce the interval time for smoother animation

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center">
      <div
        className="svg-container"
        style={{
          position: "relative",
          width: "200px",
          height: "220px",
          overflow: "hidden",
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="200"
          height="220"
          viewBox="0 0 200 220"
          fill="none"
        >
          <defs>
            <mask id="progress-mask">
              <rect
                x="0"
                y={`${220 - (progress / 100) * 220}`}
                width="200"
                height={`${(progress / 100) * 220}`}
                fill="white"
              />
            </mask>
          </defs>
          <path
            d="M125.861 21.0113C114.955 6.5776 95.9168 -1.91895 75.3774 0.370478C46.7567 3.5607 25.5907 26.5386 26.4929 52.5556C10.5098 60.3448 0.034907 74.2435 0.515895 89.7694C1.12367 109.388 19.0303 125.331 42.7059 129.251C49.1193 140.586 59.8454 147.878 71.7719 147.509C80.6732 147.233 88.643 142.739 94.4829 135.608C94.5242 145.186 94.3782 155.497 93.8086 164.051C92.2767 187.057 89.2129 216.587 89.2129 216.587L113.213 219.334C113.213 219.334 110.092 183.767 111.713 164.051C112.263 157.358 114.801 148.79 117.915 140.39C123.606 145.084 130.464 147.735 137.772 147.509C152.122 147.064 164.051 135.656 168.538 119.915C185.952 114.526 198.317 102.584 199.263 87.9903C200.114 74.8738 191.541 62.6893 177.764 54.8445C170.625 34.474 149.7 20.5463 125.861 21.0113Z"
            fill="#FAFAFA"
          />
          <path
            d="M125.861 21.0113C114.955 6.5776 95.9168 -1.91895 75.3774 0.370478C46.7567 3.5607 25.5907 26.5386 26.4929 52.5556C10.5098 60.3448 0.034907 74.2435 0.515895 89.7694C1.12367 109.388 19.0303 125.331 42.7059 129.251C49.1193 140.586 59.8454 147.878 71.7719 147.509C80.6732 147.233 88.643 142.739 94.4829 135.608C94.5242 145.186 94.3782 155.497 93.8086 164.051C92.2767 187.057 89.2129 216.587 89.2129 216.587L113.213 219.334C113.213 219.334 110.092 183.767 111.713 164.051C112.263 157.358 114.801 148.79 117.915 140.39C123.606 145.084 130.464 147.735 137.772 147.509C152.122 147.064 164.051 135.656 168.538 119.915C185.952 114.526 198.317 102.584 199.263 87.9903C200.114 74.8738 191.541 62.6893 177.764 54.8445C170.625 34.474 149.7 20.5463 125.861 21.0113Z"
            fill="#1F4444"
            mask="url(#progress-mask)"
          />
        </svg>
      </div>
      <svg
        className="-mt-4"
        xmlns="http://www.w3.org/2000/svg"
        width="91"
        height="27"
        viewBox="0 0 91 27"
        fill="none"
      >
        <ellipse
          cx="45.2722"
          cy="13.5666"
          rx="45.1341"
          ry="13.0839"
          transform="rotate(-0.616054 45.2722 13.5666)"
          fill="#EDEDED"
        />
      </svg>
    </div>
  );
};
